import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ORCAMENTO_HEADER } from "@/lib/fiscal";
import { Plus, Eye, Trash2, FileText, X as XIcon, CheckCircle2, Share2, ArrowRightCircle, MoreHorizontal, Mail, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { SemDataFields, periodoOrcamento } from "@/components/orcamentos/SemDataFields";

const STATUS_LABEL = {
  rascunho: "Rascunho", enviado: "Enviado", aprovado: "Aprovado",
  recusado: "Recusado", expirado: "Expirado",
};
const STATUS_TONE = {
  rascunho: "bg-stone-100 text-stone-700",
  enviado: "bg-[#EAF0EA] text-[#083A6B]",
  aprovado: "bg-[#EAF0EA] text-[#083A6B]",
  recusado: "bg-[#FBE7DE] text-[#D4A652]",
  expirado: "bg-[#FDF3E3] text-[#8B6A1F]",
};
const CAFE_OPTS = [
  { key: "nenhum", label: "Sem café", price: 0 },
  { key: "simples", label: "Panificadora Jacaré", price: 22 },
  { key: "donna_italia", label: "Donna Italia", price: 32 },
  { key: "manual", label: "Valor manual", price: null },
];
const NONE = "__none__";

function todayStr() { return new Date().toISOString().slice(0, 10); }
function addDaysStr(base, n) { const d = new Date(base + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }
function diffDays(a, b) {
  if (!a || !b) return 0;
  const d = Math.round((new Date(b) - new Date(a)) / 86400000);
  return Number.isFinite(d) ? Math.max(0, d) : 0;
}

function emptyForm() {
  return {
    cliente_mode: "existente",       // "existente" | "manual"
    hospede_id: "", empresa_id: "", pousada_id: "",
    cliente_manual: { nome: "", telefone: "", email: "", cpf_cnpj: "" },
    itens: [], data_entrada: todayStr(), data_saida: addDaysStr(todayStr(), 1),
    sem_data: false, modo_valor: "noites", noites_estimadas: "", valor_hospedagem_manual: "",
    num_pessoas: 2, cafe_tipo: "nenhum", cafe_valor_pessoa_manual: "",
    desconto: "0",
    is_empresarial: false, observacao: "", status: "rascunho", validade_data: "",
  };
}

export default function Orcamentos() {
  const [orcamentos, setOrcamentos] = useState([]);
  const [hospedes, setHospedes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [pousadas, setPousadas] = useState([]);
  const [quartos, setQuartos] = useState([]);
  const [open, setOpen] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [detOpen, setDetOpen] = useState(false);
  const [detTarget, setDetTarget] = useState(null);
  const [emailHistoryFilter, setEmailHistoryFilter] = useState("");
  // F-ORÇ-MOB.5: conversão mobile com confirmação + anti-double-click
  const [converterOpen, setConverterOpen] = useState(false);
  const [converterTarget, setConverterTarget] = useState(null);
  const [converterLoading, setConverterLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      // allSettled: falha em uma lista não esvazia as demais (ex.: pousadas)
      const res = await Promise.allSettled([
        api.get("/orcamentos"), api.get("/hospedes"), api.get("/empresas"),
        api.get("/pousadas"), api.get("/quartos"),
      ]);
      const setters = [setOrcamentos, setHospedes, setEmpresas, setPousadas, setQuartos];
      const nomes = ["orçamentos", "hóspedes", "empresas", "pousadas", "quartos"];
      const falhas = [];
      res.forEach((r, i) => {
        if (r.status === "fulfilled") setters[i](r.value.data);
        else falhas.push(nomes[i]);
      });
      if (falhas.length) toast.error(`Falha ao carregar: ${falhas.join(", ")}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao carregar orçamentos"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pousadaSel = useMemo(() => pousadas.find((p) => p.id === form.pousada_id), [pousadas, form.pousada_id]);
  const quartosDaPousada = useMemo(
    () => quartos.filter((q) => q.pousada_id === form.pousada_id),
    [quartos, form.pousada_id]);
  const diarias = useMemo(
    () => (form.sem_data ? Math.max(0, Number(form.noites_estimadas || 0)) : diffDays(form.data_entrada, form.data_saida)),
    [form.sem_data, form.noites_estimadas, form.data_entrada, form.data_saida]);
  const valorManualAtivo = form.sem_data && form.modo_valor === "manual";
  const cafeValor = form.cafe_tipo === "manual"
    ? Math.max(0, Number(form.cafe_valor_pessoa_manual || 0))
    : (CAFE_OPTS.find((c) => c.key === form.cafe_tipo)?.price || 0);
  const subtotalHosp = valorManualAtivo
    ? Math.max(0, Number(form.valor_hospedagem_manual || 0))
    : form.itens.reduce((s, it) => s + Number(it.valor_diaria || 0) * diarias, 0);
  const subtotalCafe = Number(form.num_pessoas || 0) * diarias * cafeValor;
  const desc = Math.max(0, Number(form.desconto || 0));
  const descontoExcede = desc > subtotalHosp + subtotalCafe;
  const totalCalc = Math.max(0, subtotalHosp + subtotalCafe - desc);
  const noitesDasDatas = diffDays(form.data_entrada, form.data_saida);

  // ============ RH-VISUAL-03: proteção anti-descarte acidental do form ============
  // Snapshot dos dados no momento em que o modal foi aberto. Usado para detectar
  // se o usuário fez alguma alteração (dirty). Compara via JSON.stringify.
  const initialFormRef = useRef(JSON.stringify(emptyForm()));
  const [discardOpen, setDiscardOpen] = useState(false);
  const isFormDirty = useCallback(() => {
    try { return JSON.stringify(form) !== initialFormRef.current; }
    catch { return true; }
  }, [form]);
  // attemptClose é a única forma de fechar o form dialog. Se sujo → confirma.
  const attemptClose = useCallback(() => {
    if (isFormDirty()) setDiscardOpen(true);
    else { setOpen(false); setEditingId(null); setForm(emptyForm()); }
  }, [isFormDirty]);
  const confirmDiscard = useCallback(() => {
    setDiscardOpen(false);
    setOpen(false); setOpenMobile(false); setEditingId(null); setForm(emptyForm());
  }, []);

  const addItem = (quarto_id, tipo_reserva = "individual") => {
    const q = quartos.find((x) => x.id === quarto_id);
    if (!q) return;
    if (form.itens.some((it) => it.quarto_id === quarto_id)) return toast.error("Quarto já adicionado");
    const preco_base = form.is_empresarial && q.preco_empresa ? q.preco_empresa : q.valor_diaria;
    setForm({ ...form, itens: [...form.itens, {
      quarto_id, tipo_reserva, valor_diaria: preco_base || 0, num_pessoas: 1,
      quarto_numero: q.numero, quarto_tipo: q.tipo,
    }] });
  };
  const removeItem = (idx) => setForm({ ...form, itens: form.itens.filter((_, i) => i !== idx) });

  const submit = async () => {
    // Cliente: existente OU manual (comercial). Nunca ambos.
    if (form.cliente_mode === "existente") {
      if (!form.hospede_id) return toast.error("Selecione um cliente existente");
    } else {
      if (!(form.cliente_manual?.nome || "").trim())
        return toast.error("Informe o nome do cliente");
    }
    if (!form.pousada_id) return toast.error("Selecione a pousada");
    if (form.itens.length === 0) return toast.error("Adicione ao menos um quarto");
    if (descontoExcede) return toast.error("O desconto não pode ser maior que o subtotal");
    if (!form.sem_data && diarias <= 0) return toast.error("Datas inválidas");
    if (form.sem_data && form.modo_valor === "noites" && diarias < 1)
      return toast.error("Informe o número estimado de noites");
    if (valorManualAtivo && !(Number(form.valor_hospedagem_manual) > 0))
      return toast.error("Informe o valor total da hospedagem");
    if (form.cafe_tipo === "manual") {
      const v = Number(form.cafe_valor_pessoa_manual);
      if (!(v >= 0) || form.cafe_valor_pessoa_manual === "" || Number.isNaN(v))
        return toast.error("Informe o valor por pessoa do café (>= 0)");
    }
    const payload = {
      empresa_id: form.empresa_id || null,
      pousada_id: form.pousada_id,
      itens: form.itens.map((it) => ({
        quarto_id: it.quarto_id, tipo_reserva: it.tipo_reserva,
        valor_diaria: Number(it.valor_diaria), num_pessoas: Number(it.num_pessoas || 1),
      })),
      data_entrada: form.sem_data ? null : form.data_entrada,
      data_saida: form.sem_data ? null : form.data_saida,
      sem_data: !!form.sem_data,
      modo_valor: form.sem_data ? form.modo_valor : null,
      noites_estimadas: form.noites_estimadas !== "" ? Number(form.noites_estimadas) : null,
      valor_hospedagem_manual: valorManualAtivo ? Number(form.valor_hospedagem_manual) : null,
      num_pessoas: Number(form.num_pessoas || 1),
      cafe_tipo: form.cafe_tipo, desconto: desc,
      is_empresarial: !!form.is_empresarial,
      observacao: form.observacao, status: form.status,
      validade_data: form.validade_data || null,
    };
    if (form.cliente_mode === "existente") {
      payload.hospede_id = form.hospede_id;
      payload.cliente_manual = null;
    } else {
      payload.hospede_id = null;
      const cm = form.cliente_manual;
      payload.cliente_manual = {
        nome: cm.nome.trim(),
        telefone: (cm.telefone || "").trim(),
        email: (cm.email || "").trim(),
        cpf_cnpj: (cm.cpf_cnpj || "").trim(),
      };
    }
    if (form.cafe_tipo === "manual") {
      payload.cafe_valor_pessoa_manual = Number(form.cafe_valor_pessoa_manual);
    } else {
      payload.cafe_valor_pessoa_manual = null;
    }
    try {
      if (editingId) await api.patch(`/orcamentos/${editingId}`, payload);
      else await api.post("/orcamentos", payload);
      toast.success(editingId ? "Orçamento atualizado" : "Orçamento criado");
      setOpen(false); setOpenMobile(false); setEditingId(null); setForm(emptyForm());
      initialFormRef.current = JSON.stringify(emptyForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const openEdit = (o) => {
    setEditingId(o.id);
    const isManual = (o.cliente_tipo === "manual") || (!o.hospede_id && o.cliente_snapshot);
    const snap = o.cliente_snapshot || {};
    const nextForm = {
      cliente_mode: isManual ? "manual" : "existente",
      hospede_id: o.hospede_id || "", empresa_id: o.empresa_id || "",
      pousada_id: o.pousada_id,
      cliente_manual: {
        nome: snap.nome || o.hospede_nome || "",
        telefone: snap.telefone || "",
        email: snap.email || "",
        cpf_cnpj: snap.cpf_cnpj || "",
      },
      itens: (o.itens || []).map((it) => ({ ...it })),
      data_entrada: o.data_entrada || "", data_saida: o.data_saida || "",
      sem_data: !!o.sem_data, modo_valor: o.modo_valor || "noites",
      noites_estimadas: o.noites_estimadas != null ? String(o.noites_estimadas) : "",
      valor_hospedagem_manual: o.valor_hospedagem_manual != null ? String(o.valor_hospedagem_manual) : "",
      num_pessoas: o.num_pessoas, cafe_tipo: o.cafe_tipo || "nenhum",
      cafe_valor_pessoa_manual: o.cafe_tipo === "manual"
        ? String(o.cafe_valor_pessoa_manual ?? o.cafe_valor_pessoa ?? "")
        : "",
      desconto: String(o.desconto || 0), is_empresarial: !!o.is_empresarial,
      observacao: o.observacao || "", status: o.status || "rascunho",
      validade_data: o.validade_data || "",
    };
    setForm(nextForm);
    initialFormRef.current = JSON.stringify(nextForm);
    setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Excluir este orçamento?")) return;
    try { await api.delete(`/orcamentos/${id}`); toast.success("Removido"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };
  const changeStatus = async (o, novo) => {
    try { await api.patch(`/orcamentos/${o.id}`, { status: novo }); toast.success(`Status → ${STATUS_LABEL[novo]}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };
  // F-ORÇ-MOB.5: abre confirmação (desktop e mobile). Não chama o endpoint imediatamente.
  const converter = (o) => {
    if (!o) return;
    if (o.status !== "aprovado") {
      toast.error("Somente orçamentos aprovados podem ser convertidos em reserva.");
      return;
    }
    if (Array.isArray(o.reserva_ids) && o.reserva_ids.length > 0) {
      toast.message("Este orçamento já foi convertido em pré-reserva.");
      return;
    }
    if (o.sem_data) {
      toast.error("Orçamento sem data definida — edite e informe as datas antes de converter.");
      return;
    }
    setConverterTarget(o);
    setConverterOpen(true);
  };

  // F-ORÇ-MOB.5: executa a conversão com anti-double-click + tratamento de status.
  const confirmarConversao = async () => {
    const o = converterTarget;
    if (!o || converterLoading) return;
    setConverterLoading(true);
    try {
      const { data } = await api.post(`/orcamentos/${o.id}/converter`);
      const qtd = Array.isArray(data?.reserva_ids) ? data.reserva_ids.length : 0;
      toast.success(`Pré-reserva criada com sucesso (${qtd} ${qtd === 1 ? "item" : "itens"}). Acesse em /reservas.`);
      setConverterOpen(false);
      setConverterTarget(null);
      setDetOpen(false);
      load();
    } catch (err) {
      const status = err?.response?.status;
      const raw = err?.response?.data?.detail;
      if (status === 400) {
        const isProspect = typeof raw === "string" && /hospede|hóspede|cadastr/i.test(raw);
        toast.error(isProspect
          ? "Este cliente ainda não está cadastrado como hóspede. Cadastre o cliente como hóspede antes de converter o orçamento."
          : (raw || "Não foi possível converter o orçamento. Verifique os dados e tente novamente."));
      } else if (status === 401) {
        toast.error("Sua sessão expirou. Faça login novamente para converter o orçamento.");
      } else if (status === 403) {
        toast.error("Você não tem permissão para converter este orçamento.");
      } else if (status === 409) {
        toast.error("Não foi possível converter porque a disponibilidade mudou. Verifique as datas e os quartos antes de continuar.");
      } else {
        toast.error(raw || "Não foi possível converter o orçamento agora. Tente novamente em instantes.");
      }
    } finally {
      setConverterLoading(false);
    }
  };
  const fecharConverter = () => {
    if (converterLoading) return; // não fecha durante loading
    setConverterOpen(false);
    setConverterTarget(null);
  };
  const gerarPdf = async (o) => {
    try {
      const { data } = await api.get(`/orcamentos/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar PDF");
    }
  };
  const compartilharWhatsapp = async (o) => {
    const msg = "Olá! Segue o orçamento da Rede Hospeda referente à sua hospedagem. Caso tenha alguma dúvida, estamos à disposição.";
    try {
      // 1) Preferência: Web Share API com o próprio arquivo PDF
      const pdfResp = await api.get(`/orcamentos/${o.id}/pdf`, { responseType: "blob" });
      const file = new File([pdfResp.data],
        `orcamento_${o.numero || o.id}.pdf`, { type: "application/pdf" });
      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Orçamento ${o.numero}`, text: msg });
        toast.success("Compartilhamento iniciado. Finalize o envio no WhatsApp.");
        return;
      }
      // 2) Fallback: link público seguro (token opaco 256-bit, TTL 7 dias) + wa.me
      const url = orcShareUrl
        || (await api.post(`/orcamentos/${o.id}/share-link`)).data?.share_url;
      if (url && url !== orcShareUrl) setOrcShareUrl(url);
      const texto = `${msg}\n\n${url}`;
      const wa = `https://wa.me/?text=${encodeURIComponent(texto)}`;
      window.open(wa, "_blank");
      toast.success("WhatsApp aberto. Selecione o cliente e envie a mensagem.");
    } catch (err) {
      if (err?.name === "AbortError") return; // usuário cancelou o share
      toast.error(err?.response?.data?.detail || "Não foi possível compartilhar. Tente Gerar PDF e enviar manualmente.");
    }
  };

  // F-ORÇ-MOB.4: Compartilhamento nativo mobile — usa a folha de share do SO (PDF > URL > clipboard).
  // Reaproveita share-link/PDF existentes; não altera backend nem regras.
  const compartilharNativo = async (o) => {
    const msg = "Olá! Segue o orçamento da Rede Hospeda referente à sua hospedagem. Caso tenha alguma dúvida, estamos à disposição.";
    const isAbort = (err) => err && (err.name === "AbortError" || /aborted|cancel/i.test(err.message || ""));
    try {
      // 1) Tentar compartilhar o próprio PDF (Web Share Level 2)
      let pdfFile = null;
      try {
        const pdfResp = await api.get(`/orcamentos/${o.id}/pdf`, { responseType: "blob" });
        pdfFile = new File([pdfResp.data], `orcamento_${o.numero || o.id}.pdf`, { type: "application/pdf" });
      } catch { /* PDF pode falhar; tenta URL share */ }
      if (pdfFile && typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        try {
          await navigator.share({ files: [pdfFile], title: `Orçamento ${o.numero}`, text: msg });
          toast.success("Compartilhamento iniciado.");
          return;
        } catch (err) {
          if (isAbort(err)) return; // cancelamento pelo usuário — não é erro
          // cai para URL share
        }
      }
      // 2) Garantir URL pública (share-link existente ou gerar novo)
      let url = orcShareUrl;
      if (!url) {
        const { data } = await api.post(`/orcamentos/${o.id}/share-link`);
        url = data?.share_url;
        if (url) setOrcShareUrl(url);
      }
      if (!url) throw new Error("no-url");
      // 3) Compartilhar a URL (Web Share Level 1)
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        try {
          await navigator.share({ url, title: `Orçamento ${o.numero}`, text: msg });
          toast.success("Compartilhamento iniciado.");
          return;
        } catch (err) {
          if (isAbort(err)) return;
          // cai para clipboard
        }
      }
      // 4) Fallback clipboard (Clipboard API + textarea execCommand)
      let copied = false;
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url); copied = true;
        }
      } catch { /* fallback textarea */ }
      if (!copied) {
        try {
          const ta = document.createElement("textarea");
          ta.value = url; ta.setAttribute("readonly", "");
          ta.style.position = "absolute"; ta.style.left = "-9999px";
          document.body.appendChild(ta); ta.select();
          copied = document.execCommand && document.execCommand("copy");
          document.body.removeChild(ta);
        } catch { copied = false; }
      }
      if (copied) toast.success("Link copiado! Cole no WhatsApp ou e-mail.");
      else toast.message("Compartilhamento nativo indisponível. Use WhatsApp ou Copiar link.");
    } catch (err) {
      if (isAbort(err)) return;
      toast.error(err?.response?.data?.detail || "Não foi possível compartilhar agora.");
    }
  };
  const revogarCompartilhamento = async (o) => {
    if (!window.confirm("Deseja revogar todos os links de compartilhamento deste orçamento?")) return;
    try {
      const { data } = await api.post(`/orcamentos/${o.id}/revoke-shares`);
      if (data.revoked_count === 0) toast.message("Não havia links ativos para revogar.");
      else toast.success(`${data.revoked_count} link(s) revogado(s). Novos links podem ser gerados.`);
      setOrcShareUrl(null); // limpa cache — próximo compartilhamento gera novo token
      setQrOpen(false); // fecha QR — o link mostrado seria inválido
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao revogar");
    }
  };
  const abrirQrCode = async (o) => {
    // Reutiliza o share_url em cache; só gera novo token se ainda não houver.
    // Abrir/fechar o QR NÃO gera múltiplos tokens.
    if (qrOpen) { setQrOpen(false); return; }
    try {
      if (!orcShareUrl) {
        setQrLoading(true);
        const { data } = await api.post(`/orcamentos/${o.id}/share-link`);
        if (data?.share_url) setOrcShareUrl(data.share_url);
      }
      setQrOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível gerar o link agora.");
    } finally {
      setQrLoading(false);
    }
  };
  const copiarLink = async (o) => {
    // Reutiliza o share_url em cache se já foi gerado nesta sessão de detalhe.
    // Só faz POST /share-link (que gera novo token) se ainda não houver um.
    try {
      let url = orcShareUrl;
      if (!url) {
        const { data } = await api.post(`/orcamentos/${o.id}/share-link`);
        url = data?.share_url;
        setOrcShareUrl(url);
      }
      if (!url) throw new Error("no-url");
      // Copiar via Clipboard API (com fallback silencioso)
      let copied = false;
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch { /* cai no fallback abaixo */ }
      if (!copied) {
        // Fallback usando textarea temporário — funciona em contextos sem HTTPS Clipboard API
        try {
          const ta = document.createElement("textarea");
          ta.value = url; ta.setAttribute("readonly", "");
          ta.style.position = "absolute"; ta.style.left = "-9999px";
          document.body.appendChild(ta); ta.select();
          copied = document.execCommand && document.execCommand("copy");
          document.body.removeChild(ta);
        } catch { copied = false; }
      }
      if (copied) {
        toast.success("Link copiado!");
        setCopyFeedback({ kind: "ok", text: "Link copiado!" });
      } else {
        toast.error("Não foi possível copiar automaticamente. Copie manualmente pelo WhatsApp.");
        setCopyFeedback({ kind: "fail", text: "Não foi possível copiar automaticamente." });
      }
      setTimeout(() => setCopyFeedback(null), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível gerar o link agora.");
      setCopyFeedback({ kind: "fail", text: "Não foi possível gerar o link agora." });
      setTimeout(() => setCopyFeedback(null), 2500);
    }
  };
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDialogTarget, setEmailDialogTarget] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailInputError, setEmailInputError] = useState("");
  const [orcShareUrl, setOrcShareUrl] = useState(null); // cache do share_url do orçamento aberto
  const [copyFeedback, setCopyFeedback] = useState(null); // {kind: "ok"|"fail", text}
  const [openFailureKey, setOpenFailureKey] = useState(null); // {id}::{index} — só um motivo aberto por vez
  const [qrOpen, setQrOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [aba, setAba] = useState("lista"); // "lista" | "diagnostico"
  const [diagPeriodoDe, setDiagPeriodoDe] = useState("");
  const [diagPeriodoAte, setDiagPeriodoAte] = useState("");
  const [diagPousada, setDiagPousada] = useState("");
  const [diagStatus, setDiagStatus] = useState("todos"); // todos|sent|failed
  const [diagDest, setDiagDest] = useState("");
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  // Mapeamento amigável de status técnicos → texto legível ao usuário.
  // Nunca expor stack trace, código bruto ou detalhes de infra.
  const friendlyFailureReason = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "bounced")
      return "O endereço de e-mail não aceitou a mensagem. Confira o endereço e tente novamente.";
    if (s === "failed" || s === "error" || s === "")
      return "Não foi possível enviar este e-mail. Você pode tentar novamente.";
    // Qualquer outro status inesperado — mensagem genérica e segura
    return "Não foi possível enviar este e-mail. Você pode tentar novamente.";
  };

  const enviarEmail = (o, options = {}) => {
    const hosp = hospedes.find((h) => h.id === o.hospede_id);
    const cadastroEmail = (hosp?.email || "").trim();
    const preset = (options.preset || "").trim();
    const isReenvio = !!preset;
    // Reenvio força modo override para que o usuário confirme/edite o destinatário.
    const forceOverride = isReenvio;
    setEmailDialogTarget({ orc: o, cadastroEmail, forceOverride, isReenvio });
    setEmailInput(preset || cadastroEmail || "");
    setEmailInputError("");
    setEmailDialogOpen(true);
  };

  const confirmarEnvioEmail = async () => {
    if (!emailDialogTarget) return;
    const { orc, cadastroEmail, forceOverride } = emailDialogTarget;
    const usaOverride = forceOverride || !cadastroEmail;
    const emailFinal = (usaOverride ? emailInput : cadastroEmail).trim();
    if (!emailFinal || !EMAIL_RE.test(emailFinal)) {
      setEmailInputError("Informe um e-mail válido.");
      return;
    }
    setEmailInputError("");
    setEmailSending(true);
    try {
      const body = usaOverride ? { email_override: emailFinal } : {};
      const { data } = await api.post(`/orcamentos/${orc.id}/enviar-email`, body);
      toast.success(`E-mail enviado para ${data.sent_to}.`);
      setEmailDialogOpen(false);
      setEmailDialogTarget(null);
      setEmailInput("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar e-mail");
    } finally {
      setEmailSending(false);
    }
  };

  const openDet = (o) => { setDetTarget(o); setDetOpen(true); };

  return (
    <div data-testid="orcamentos-page">
      <PageHeader eyebrow="Comercial" title="Orçamentos"
        description="Monte cotações para clientes com cabeçalho profissional. Orçamentos NÃO geram reserva nem receita — só uma reserva confirmada aparece no financeiro." />

      {/* Tabs: Lista | Diagnóstico (read-only) */}
      <div className="flex items-center gap-1 mb-4 border-b border-stone-200" data-testid="orc-tabs">
        <button data-testid="tab-orc-lista"
          onClick={() => setAba("lista")}
          className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${
            aba === "lista" ? "border-[#083A6B] text-[#083A6B] font-semibold" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
          Lista
        </button>
        <button data-testid="tab-orc-diagnostico"
          onClick={() => setAba("diagnostico")}
          className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${
            aba === "diagnostico" ? "border-[#083A6B] text-[#083A6B] font-semibold" : "border-transparent text-stone-500 hover:text-stone-800"}`}>
          Diagnóstico
        </button>
      </div>

      {aba === "diagnostico" && (() => {
        // Achata email_history[] de todos os orçamentos em linhas planas.
        const entries = [];
        for (const o of orcamentos) {
          for (const [i, e] of (o.email_history || []).entries()) {
            entries.push({
              key: `${o.id}::${i}`,
              orc: o, i, sent_at: e.sent_at || "", to: e.to || "",
              status: (e.status || "").toLowerCase(),
              error_code: e.error_code || "",
            });
          }
        }
        // Filtros
        const de = diagPeriodoDe ? new Date(diagPeriodoDe + "T00:00:00") : null;
        const ate = diagPeriodoAte ? new Date(diagPeriodoAte + "T23:59:59") : null;
        const q = (diagDest || "").trim().toLowerCase();
        const filtered = entries.filter((e) => {
          if (diagPousada && e.orc.pousada_id !== diagPousada) return false;
          if (diagStatus !== "todos" && e.status !== diagStatus) return false;
          if (q && !e.to.toLowerCase().includes(q)) return false;
          if (de || ate) {
            const t = new Date(e.sent_at);
            if (de && t < de) return false;
            if (ate && t > ate) return false;
          }
          return true;
        }).sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || ""));
        // Resumo
        const total = filtered.length;
        const falhas = filtered.filter((e) => e.status === "failed").length;
        const enviados = filtered.filter((e) => e.status === "sent").length;
        const taxa = total > 0 ? Math.round((falhas / total) * 1000) / 10 : 0;
        return (
          <div className="space-y-4" data-testid="orc-diagnostico-panel">
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 p-3 border border-stone-200 rounded-md bg-[#FFFFFF]">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">De</Label>
                <Input type="date" data-testid="diag-periodo-de" value={diagPeriodoDe}
                  onChange={(e) => setDiagPeriodoDe(e.target.value)} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Até</Label>
                <Input type="date" data-testid="diag-periodo-ate" value={diagPeriodoAte}
                  onChange={(e) => setDiagPeriodoAte(e.target.value)} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Pousada</Label>
                <select data-testid="diag-pousada" value={diagPousada}
                  onChange={(e) => setDiagPousada(e.target.value)}
                  className="w-full h-8 text-xs border border-stone-300 rounded-md px-2 bg-white">
                  <option value="">Todas</option>
                  {pousadas.map((p) => (<option key={p.id} value={p.id}>{p.nome}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Status</Label>
                <select data-testid="diag-status" value={diagStatus}
                  onChange={(e) => setDiagStatus(e.target.value)}
                  className="w-full h-8 text-xs border border-stone-300 rounded-md px-2 bg-white">
                  <option value="todos">Todos</option>
                  <option value="sent">Enviado</option>
                  <option value="failed">Falhou</option>
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Destinatário</Label>
                <Input type="search" data-testid="diag-dest" value={diagDest}
                  onChange={(e) => setDiagDest(e.target.value)}
                  placeholder="ex: cliente@..." className="h-8 text-xs" />
              </div>
            </div>

            {/* Cards de resumo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div data-testid="diag-card-total" className="border border-stone-200 rounded-md p-3 bg-white">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total de envios</div>
                <div className="text-2xl font-semibold text-stone-800 mt-1">{total}</div>
                <div className="text-[10px] text-stone-400 mt-1">{enviados} enviado(s)</div>
              </div>
              <div data-testid="diag-card-falhas" className="border border-stone-200 rounded-md p-3 bg-white">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Falhas</div>
                <div className="text-2xl font-semibold text-[#D4A652] mt-1">{falhas}</div>
              </div>
              <div data-testid="diag-card-taxa" className="border border-stone-200 rounded-md p-3 bg-white">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Taxa de falha</div>
                <div className="text-2xl font-semibold text-stone-800 mt-1">{taxa}%</div>
              </div>
            </div>

            {/* Exportar CSV — usa exatamente o mesmo dataset já filtrado (sem chamar API) */}
            <div className="flex justify-end">
              <button
                data-testid="btn-diag-exportar-csv"
                onClick={() => {
                  const escCsv = (v) => {
                    const s = v === null || v === undefined ? "" : String(v);
                    return /[",;\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
                  };
                  const header = ["Data/Hora","Orçamento","Hóspede/Cliente","Pousada","Destinatário","Status","Motivo da falha"];
                  const rows = filtered.map((e) => {
                    let quando = e.sent_at || "";
                    try { if (e.sent_at) quando = new Date(e.sent_at).toLocaleString("pt-BR"); } catch {}
                    const isSent = e.status === "sent";
                    const cli = e.orc.empresa_nome
                      ? `${e.orc.hospede_nome || ""} (${e.orc.empresa_nome})`
                      : (e.orc.hospede_nome || "");
                    return [
                      quando,
                      e.orc.numero || "",
                      cli,
                      e.orc.pousada_nome || "",
                      e.to || "",
                      isSent ? "Enviado" : "Falhou",
                      isSent ? "" : friendlyFailureReason(e.error_code || e.status),
                    ];
                  });
                  const csv = [header, ...rows].map((r) => r.map(escCsv).join(",")).join("\r\n");
                  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  const d = new Date();
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, "0");
                  const dd = String(d.getDate()).padStart(2, "0");
                  a.href = url;
                  a.download = `historico-emails-${yyyy}-${mm}-${dd}.csv`;
                  document.body.appendChild(a); a.click();
                  document.body.removeChild(a);
                  setTimeout(() => URL.revokeObjectURL(url), 500);
                }}
                disabled={filtered.length === 0}
                className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md border border-[#083A6B]/40 text-[#083A6B] bg-white hover:bg-[#EAF0EA] disabled:opacity-50 disabled:cursor-not-allowed">
                ↓ Exportar CSV
              </button>
            </div>

            {/* Tabela */}
            <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50 hover:bg-stone-50">
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Data/hora</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Orçamento</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Cliente</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Destinatário</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((e) => {
                    let quando = e.sent_at;
                    try { if (e.sent_at) quando = new Date(e.sent_at).toLocaleString("pt-BR"); } catch {}
                    const ok = e.status === "sent";
                    return (
                      <TableRow key={e.key} data-testid={`diag-row-${e.orc.id}-${e.i}`}>
                        <TableCell className="text-xs font-mono text-stone-500 whitespace-nowrap">{quando}</TableCell>
                        <TableCell className="font-mono text-xs">{e.orc.numero}</TableCell>
                        <TableCell className="text-xs">{e.orc.hospede_nome}
                          {e.orc.empresa_nome && <div className="text-[10px] text-stone-500">{e.orc.empresa_nome}</div>}
                        </TableCell>
                        <TableCell className="text-xs">{e.orc.pousada_nome}</TableCell>
                        <TableCell className="text-xs">{e.to || "—"}</TableCell>
                        <TableCell>
                          {ok ? (
                            <span data-testid={`diag-badge-sent-${e.orc.id}-${e.i}`}
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#EAF0EA] text-[#083A6B]">
                              Enviado
                            </span>
                          ) : (
                            <span data-testid={`diag-badge-failed-${e.orc.id}-${e.i}`}
                              title={e.error_code ? `Status técnico: ${e.error_code}` : undefined}
                              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4A652] text-white font-semibold">
                              Falhou
                            </span>
                          )}
                          {!ok && (
                            <div className="text-[10px] text-stone-500 mt-1 max-w-xs">
                              {friendlyFailureReason(e.error_code || e.status)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <button data-testid={`btn-diag-view-${e.orc.id}-${e.i}`}
                            onClick={() => openDet(e.orc)}
                            className="text-stone-500 hover:text-stone-800 p-1" title="Abrir orçamento">
                            <Eye size={14} />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-stone-500 text-xs py-6"
                        data-testid="diag-empty">
                        Nenhum envio encontrado com os filtros atuais.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        );
      })()}

      {aba === "lista" && (<>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-widest text-stone-400">
          {orcamentos.length} orçamento(s)
        </div>
        <Button data-testid="btn-novo-orcamento"
          onClick={() => {
            const empty = emptyForm();
            setEditingId(null); setForm(empty);
            initialFormRef.current = JSON.stringify(empty);
            const isMobile = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 767px)").matches;
            if (isMobile) setOpenMobile(true);
            else setOpen(true);
          }}
          className="bg-[#083A6B] hover:bg-[#22392E] text-white">
          <Plus size={16} className="mr-2" /> Novo orçamento
        </Button>
      </div>

      {/* ============ MOBILE (cards) — < md ============ */}
      <div className="md:hidden space-y-3" data-testid="orc-cards-mobile">
        {orcamentos.length === 0 && (
          <div data-testid="orc-cards-empty"
            className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-8 text-center text-sm text-stone-500">
            Nenhum orçamento ainda. Toque em <b>Novo orçamento</b>.
          </div>
        )}
        {orcamentos.map((o) => {
          const isAprovado = o.status === "aprovado";
          const jaConvertido = Array.isArray(o.reserva_ids) && o.reserva_ids.length > 0;
          return (
            <div key={o.id} data-testid={`orc-card-${o.id}`}
              className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4 shadow-sm">
              {/* Cabeçalho: número + status */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="font-mono text-[11px] text-stone-500">{o.numero}</div>
                  <div className="font-medium text-stone-900 text-base truncate">{o.hospede_nome || "—"}</div>
                  {o.empresa_nome && (
                    <div className="text-[11px] text-stone-500 truncate">{o.empresa_nome}</div>
                  )}
                </div>
                <span data-testid={`orc-card-status-${o.id}`}
                  className={`shrink-0 inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${STATUS_TONE[o.status] || ""}`}>
                  {STATUS_LABEL[o.status] || o.status}
                </span>
              </div>

              {/* Corpo: pousada, período, validade */}
              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Pousada</div>
                  <div className="text-stone-800 text-sm truncate">{o.pousada_nome || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Total</div>
                  <div className="font-serif text-lg text-[#083A6B]" data-testid={`orc-card-total-${o.id}`}>{brl(o.total)}</div>
                </div>
                {o.sem_data ? (
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-stone-400">Período</div>
                    <div className="text-stone-800 text-sm" data-testid={`orc-card-periodo-${o.id}`}>{periodoOrcamento(o, fmtDate)}</div>
                  </div>
                ) : (<>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Entrada</div>
                  <div className="text-stone-800 text-sm">{fmtDate(o.data_entrada)}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400">Saída</div>
                  <div className="text-stone-800 text-sm">{fmtDate(o.data_saida)}</div>
                </div>
                </>)}
                {o.validade_data && (
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-widest text-stone-400">Validade</div>
                    <div className="text-stone-800 text-sm">{fmtDate(o.validade_data)}</div>
                  </div>
                )}
              </div>

              {/* Ações principais — grandes e tocáveis */}
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid={`btn-card-view-${o.id}`} variant="outline"
                  onClick={() => openDet(o)} className="h-10 text-sm">
                  <Eye size={16} className="mr-2" /> Ver
                </Button>
                <Button data-testid={`btn-card-editar-${o.id}`} variant="outline"
                  onClick={() => openEdit(o)} className="h-10 text-sm">
                  Editar
                </Button>
                <Button data-testid={`btn-card-pdf-${o.id}`} variant="outline"
                  onClick={() => gerarPdf(o)} className="h-10 text-sm">
                  <FileText size={16} className="mr-2" /> PDF
                </Button>
                <Button data-testid={`btn-card-compartilhar-${o.id}`} variant="outline"
                  onClick={() => compartilharNativo(o)} className="h-10 text-sm">
                  <Share2 size={16} className="mr-2" /> Compartilhar
                </Button>
                {isAprovado && !jaConvertido && (
                  <Button data-testid={`btn-card-converter-${o.id}`}
                    onClick={() => converter(o)}
                    className="col-span-2 h-11 bg-[#083A6B] hover:bg-[#22392E] text-white">
                    <ArrowRightCircle size={16} className="mr-2" /> Converter em reserva
                  </Button>
                )}
              </div>

              {/* Mais (ações secundárias) */}
              <details data-testid={`orc-card-mais-${o.id}`} className="mt-3">
                <summary className="cursor-pointer select-none text-xs uppercase tracking-widest text-stone-500 hover:text-stone-800 flex items-center gap-1 py-2">
                  <MoreHorizontal size={14} /> Mais ações
                </summary>
                <div className="grid grid-cols-1 gap-2 mt-2 pt-2 border-t border-stone-200">
                  <Button data-testid={`btn-card-copiar-link-${o.id}`} variant="outline"
                    onClick={() => copiarLink(o)} className="h-9 text-sm justify-start">
                    <Share2 size={14} className="mr-2" /> Copiar link
                  </Button>
                  <Button data-testid={`btn-card-whatsapp-${o.id}`} variant="outline"
                    onClick={() => compartilharWhatsapp(o)} className="h-9 text-sm justify-start">
                    <Share2 size={14} className="mr-2" /> Enviar por WhatsApp
                  </Button>
                  <Button data-testid={`btn-card-email-${o.id}`} variant="outline"
                    onClick={() => enviarEmail(o)} className="h-9 text-sm justify-start">
                    <Mail size={14} className="mr-2" /> Enviar por e-mail
                  </Button>
                  <Button data-testid={`btn-card-excluir-${o.id}`} variant="outline"
                    onClick={() => remove(o.id)}
                    className="h-9 text-sm justify-start text-[#D4A652] hover:text-[#8B3A22] border-[#D4A652]/30">
                    <Trash2 size={14} className="mr-2" /> Excluir
                  </Button>
                </div>
              </details>
            </div>
          );
        })}
      </div>

      {/* ============ DESKTOP (tabela original) — >= md ============ */}
      <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50 hover:bg-stone-50">
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nº</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Cliente</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Período</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Total</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {orcamentos.map((o) => (
              <TableRow key={o.id} data-testid={`orc-row-${o.id}`}>
                <TableCell className="font-mono text-xs">{o.numero}</TableCell>
                <TableCell className="font-medium text-stone-800">
                  {o.hospede_nome}
                  {o.empresa_nome && <div className="text-[10px] text-stone-500">{o.empresa_nome}</div>}
                </TableCell>
                <TableCell>{o.pousada_nome}</TableCell>
                <TableCell className="whitespace-nowrap text-xs" data-testid={`orc-periodo-${o.id}`}>
                  {o.sem_data ? periodoOrcamento(o, fmtDate) : `${fmtDate(o.data_entrada)} → ${fmtDate(o.data_saida)} · ${o.diarias} noite(s)`}
                </TableCell>
                <TableCell>
                  <span data-testid={`orc-status-${o.id}`}
                    className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_TONE[o.status] || ""}`}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </TableCell>
                <TableCell className="text-right font-medium text-[#083A6B]">{brl(o.total)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`btn-view-orc-${o.id}`} onClick={() => openDet(o)}
                      className="text-stone-500 hover:text-stone-800 p-1" title="Ver">
                      <Eye size={14} />
                    </button>
                    <button data-testid={`btn-editar-orc-${o.id}`} onClick={() => openEdit(o)}
                      className="text-stone-500 hover:text-stone-800 text-xs px-2 py-1">
                      Editar
                    </button>
                    <button data-testid={`btn-excluir-orc-${o.id}`} onClick={() => remove(o.id)}
                      className="text-stone-400 hover:text-[#D4A652] p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {orcamentos.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-stone-500">
                  Nenhum orçamento ainda. Clique em <b>Novo orçamento</b>.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      </>)}

      {/* ============ Formulário ============ */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) attemptClose(); else setOpen(true); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="orc-form-dialog"
          onPointerDownOutside={(e) => { if (isFormDirty()) { e.preventDefault(); setDiscardOpen(true); } }}
          onInteractOutside={(e) => { if (isFormDirty()) { e.preventDefault(); setDiscardOpen(true); } }}
          onEscapeKeyDown={(e) => { if (isFormDirty()) { e.preventDefault(); setDiscardOpen(true); } }}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingId ? "Editar" : "Novo"} orçamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Cliente: existente OU manual (comercial/prospect) */}
            <div className="border border-stone-200 rounded-md p-3 bg-[#FFFFFF]">
              <div className="flex items-center gap-2 mb-2">
                <Label className="text-xs uppercase tracking-widest text-stone-500">Cliente</Label>
                <div className="inline-flex rounded-md border border-stone-200 overflow-hidden text-xs">
                  <button type="button" data-testid="orc-cliente-mode-existente"
                    onClick={() => setForm({ ...form, cliente_mode: "existente" })}
                    className={`px-3 py-1 ${form.cliente_mode === "existente"
                      ? "bg-[#083A6B] text-white" : "bg-white text-stone-600"}`}>
                    Existente (hóspede)
                  </button>
                  <button type="button" data-testid="orc-cliente-mode-manual"
                    onClick={() => setForm({ ...form, cliente_mode: "manual" })}
                    className={`px-3 py-1 ${form.cliente_mode === "manual"
                      ? "bg-[#083A6B] text-white" : "bg-white text-stone-600"}`}>
                    Novo cliente (comercial)
                  </button>
                </div>
              </div>
              {form.cliente_mode === "existente" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Hóspede</Label>
                    <Select value={form.hospede_id} onValueChange={(v) => setForm({ ...form, hospede_id: v })}>
                      <SelectTrigger data-testid="orc-select-hospede"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {hospedes.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Empresa (opcional)</Label>
                    <Select value={form.empresa_id || NONE}
                      onValueChange={(v) => setForm({ ...form, empresa_id: v === NONE ? "" : v })}>
                      <SelectTrigger data-testid="orc-select-empresa"><SelectValue placeholder="Sem empresa" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Sem empresa</SelectItem>
                        {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-[10px] text-stone-500 italic mb-2">
                    Cliente comercial ≠ hóspede. Nenhuma reserva ou hóspede será criado agora.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Nome / Razão social *</Label>
                      <Input data-testid="orc-cm-nome" value={form.cliente_manual.nome}
                        onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, nome: e.target.value }})} />
                    </div>
                    <div>
                      <Label>WhatsApp / Telefone</Label>
                      <Input data-testid="orc-cm-telefone" value={form.cliente_manual.telefone}
                        onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, telefone: e.target.value }})} />
                    </div>
                    <div>
                      <Label>E-mail</Label>
                      <Input data-testid="orc-cm-email" type="email" value={form.cliente_manual.email}
                        onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, email: e.target.value }})} />
                    </div>
                    <div>
                      <Label>CPF / CNPJ</Label>
                      <Input data-testid="orc-cm-cpf-cnpj" value={form.cliente_manual.cpf_cnpj}
                        onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, cpf_cnpj: e.target.value }})} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Empresa (opcional)</Label>
                      <Select value={form.empresa_id || NONE}
                        onValueChange={(v) => setForm({ ...form, empresa_id: v === NONE ? "" : v })}>
                        <SelectTrigger data-testid="orc-select-empresa"><SelectValue placeholder="Sem empresa" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>Sem empresa</SelectItem>
                          {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Pousada</Label>
                <Select value={form.pousada_id} onValueChange={(v) => setForm({ ...form, pousada_id: v, itens: [] })}>
                  <SelectTrigger data-testid="orc-select-pousada"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!form.sem_data && (<>
              <div>
                <Label>Entrada</Label>
                <Input type="date" value={form.data_entrada} data-testid="orc-input-entrada"
                  onChange={(e) => setForm({ ...form, data_entrada: e.target.value })} />
              </div>
              <div>
                <Label>Saída</Label>
                <Input type="date" value={form.data_saida} data-testid="orc-input-saida"
                  onChange={(e) => setForm({ ...form, data_saida: e.target.value })} />
              </div>
              </>)}
            </div>
            <SemDataFields form={form} setForm={setForm} prefix="orc" noitesDasDatas={noitesDasDatas} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>Pessoas</Label>
                <Input type="number" min="1" value={form.num_pessoas} data-testid="orc-input-pessoas"
                  onChange={(e) => setForm({ ...form, num_pessoas: e.target.value })} />
              </div>
              <div>
                <Label>Noites</Label>
                <Input value={diarias} disabled data-testid="orc-noites" />
              </div>
              <div className="col-span-2">
                <Label>Café da manhã</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {CAFE_OPTS.map((c) => (
                    <button key={c.key} type="button" data-testid={`btn-orc-cafe-${c.key}`}
                      onClick={() => setForm({ ...form, cafe_tipo: c.key,
                        cafe_valor_pessoa_manual: c.key === "manual" ? form.cafe_valor_pessoa_manual : "" })}
                      className={`px-3 py-1.5 rounded-md border text-xs font-medium transition ${
                        form.cafe_tipo === c.key
                          ? "bg-[#083A6B] text-white border-[#083A6B]"
                          : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"
                      }`}>
                      {c.label}{c.price !== null ? ` · ${brl(c.price)}` : ""}
                    </button>
                  ))}
                </div>
                {form.cafe_tipo === "manual" && (
                  <div className="mt-2">
                    <Label className="text-xs">Valor por pessoa (R$)</Label>
                    <Input type="number" min="0" step="0.01"
                      data-testid="orc-input-cafe-manual"
                      value={form.cafe_valor_pessoa_manual}
                      onChange={(e) => setForm({ ...form, cafe_valor_pessoa_manual: e.target.value })}
                      placeholder="Ex.: 27,50 (use ponto para decimal)" />
                    <div className="text-[10px] text-stone-500 italic mt-1">
                      Valor multiplicado por {form.num_pessoas || 0} pessoa(s) × {diarias} noite(s)
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_empresarial}
                  data-testid="orc-toggle-empresarial"
                  onChange={(e) => setForm({ ...form, is_empresarial: e.target.checked })} />
                Aplicar tarifa empresarial (quando o quarto tiver `preco_empresa`)
              </label>
            </div>

            {/* Itens */}
            <div className="border-t border-stone-200 pt-3">
              <Label className="mb-2 block">Quartos do orçamento</Label>
              {form.itens.length === 0 && (
                <div className="text-xs text-stone-500 mb-2">Nenhum quarto adicionado ainda.</div>
              )}
              {form.itens.map((it, idx) => (
                <div key={idx} data-testid={`orc-item-${idx}`}
                  className="flex items-center gap-2 bg-stone-50 rounded-md p-2 mb-2 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-medium">Nº {it.quarto_numero} · {it.quarto_tipo}</div>
                    <div className="text-[10px] text-stone-500">
                      tipo: {it.tipo_reserva}
                    </div>
                  </div>
                  <Input type="number" step="0.01" value={it.valor_diaria} className="w-28"
                    disabled={valorManualAtivo}
                    data-testid={`orc-item-diaria-${idx}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm({ ...form, itens: form.itens.map((x, i) => i === idx ? { ...x, valor_diaria: v } : x) });
                    }} />
                  <button data-testid={`btn-excluir-orc-item-${idx}`}
                    onClick={() => removeItem(idx)} className="text-stone-400 hover:text-[#D4A652] p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {form.pousada_id && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <Select onValueChange={(v) => {
                    if (!v) return;
                    const [tipo, quarto_id] = v.split("::");
                    addItem(quarto_id, tipo);
                  }}>
                    <SelectTrigger data-testid="orc-add-quarto-select" className="w-72">
                      <SelectValue placeholder="+ Adicionar quarto" />
                    </SelectTrigger>
                    <SelectContent>
                      {pousadaSel?.is_chacara && quartosDaPousada.filter((q) => q.is_chacara_inteira).map((q) => (
                        <SelectItem key={q.id} value={`chacara_inteira::${q.id}`}
                          data-testid={`orc-add-ci-${q.id}`}>
                          ⭐ Chácara Inteira · cap. {q.capacidade}
                        </SelectItem>
                      ))}
                      {pousadaSel?.permite_reserva_inteira && quartosDaPousada.filter((q) => q.is_pousada_inteira).map((q) => (
                        <SelectItem key={q.id} value={`pousada_inteira::${q.id}`}
                          data-testid={`orc-add-pi-${q.id}`}>
                          🏨 Pousada Inteira · cap. {q.capacidade}
                        </SelectItem>
                      ))}
                      {quartosDaPousada.filter((q) => !q.is_chacara_inteira && !q.is_pousada_inteira).map((q) => (
                        <SelectItem key={q.id} value={`individual::${q.id}`}>

      {/* ============ F-ORÇ-MOB.5 — Confirmação de conversão em reserva ============ */}
      <Dialog open={converterOpen} onOpenChange={(v) => { if (!v) fecharConverter(); else setConverterOpen(true); }}>
        <DialogContent data-testid="orc-converter-dialog"
          className="max-w-md w-[calc(100vw-2rem)] max-h-[85dvh] overflow-y-auto"
          onPointerDownOutside={(e) => { if (converterLoading) e.preventDefault(); }}
          onInteractOutside={(e) => { if (converterLoading) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (converterLoading) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#083A6B] flex items-center gap-2">
              <ArrowRightCircle size={20} /> Converter em pré-reserva?
            </DialogTitle>
          </DialogHeader>
          {converterTarget && (
            <div className="space-y-3 py-2 text-sm">
              <div className="rounded-md border border-stone-200 bg-[#FFFFFF] p-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">Orçamento</span>
                  <span data-testid="orc-converter-numero" className="font-mono text-xs text-stone-800">{converterTarget.numero || "—"}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">Cliente</span>
                  <span data-testid="orc-converter-cliente" className="text-stone-800 truncate max-w-[65%] text-right">{converterTarget.hospede_nome || "—"}</span>
                </div>
                {converterTarget.empresa_nome && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-stone-500">Empresa</span>
                    <span className="text-stone-800 truncate max-w-[65%] text-right">{converterTarget.empresa_nome}</span>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">Pousada</span>
                  <span data-testid="orc-converter-pousada" className="text-stone-800 truncate max-w-[65%] text-right">{converterTarget.pousada_nome || "—"}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">Período</span>
                  <span data-testid="orc-converter-periodo" className="text-stone-800 text-right">
                    {periodoOrcamento(converterTarget, fmtDate)}
                    {converterTarget.diarias ? ` · ${converterTarget.diarias} noite(s)` : ""}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">Quartos</span>
                  <span data-testid="orc-converter-qtd-itens" className="text-stone-800">{Array.isArray(converterTarget.itens) ? converterTarget.itens.length : 0}</span>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-stone-200 mt-1">
                  <span className="text-[10px] uppercase tracking-widest text-stone-500">Total</span>
                  <span data-testid="orc-converter-total" className="font-serif text-lg text-[#083A6B]">{brl(converterTarget.total)}</span>
                </div>
              </div>
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <div className="text-xs leading-snug">
                  Ao confirmar, {Array.isArray(converterTarget.itens) ? converterTarget.itens.length : 0} pré-reserva(s) serão criadas em <b>/reservas</b>.
                  Nenhum pagamento, sinal ou lançamento financeiro é criado nesta etapa.
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button data-testid="btn-orc-converter-cancelar" variant="outline"
              onClick={fecharConverter} disabled={converterLoading}
              className="h-11 sm:h-10 w-full sm:w-auto">
              Cancelar
            </Button>
            <Button data-testid="btn-orc-converter-confirmar"
              onClick={confirmarConversao} disabled={converterLoading}
              className="h-11 sm:h-10 w-full sm:w-auto bg-[#083A6B] hover:bg-[#22392E] text-white">
              {converterLoading
                ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Convertendo…</>)
                : (<><ArrowRightCircle size={16} className="mr-2" /> Converter em reserva</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

                          Nº {q.numero} · {q.tipo} · cap. {q.capacidade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label>Desconto (R$)</Label>
                <Input type="number" min="0" step="0.01" value={form.desconto} data-testid="orc-input-desconto"
                  onChange={(e) => setForm({ ...form, desconto: e.target.value })} />
              </div>
              <div>
                <Label>Validade</Label>
                <Input type="date" value={form.validade_data} data-testid="orc-input-validade"
                  onChange={(e) => setForm({ ...form, validade_data: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="orc-select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={form.observacao} data-testid="orc-input-obs"
                onChange={(e) => setForm({ ...form, observacao: e.target.value })} rows={2} />
            </div>

            {/* Totais preview */}
            <div className="bg-[#FFFFFF] border border-stone-200 rounded-md p-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Hospedagem</span><span data-testid="orc-preview-hosp">{brl(subtotalHosp)}</span></div>
              <div className="flex justify-between"><span>Café da manhã</span><span data-testid="orc-preview-cafe">{brl(subtotalCafe)}</span></div>
              <div className="flex justify-between text-stone-600 text-xs"><span>Subtotal</span><span>{brl(subtotalHosp + subtotalCafe)}</span></div>
              {desc > 0 && <div className="flex justify-between text-[#D4A652]"><span>Desconto</span><span>− {brl(desc)}</span></div>}
              {descontoExcede && (
                <div data-testid="orc-aviso-desconto" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  O desconto ({brl(desc)}) é maior que o subtotal ({brl(subtotalHosp + subtotalCafe)}). Ajuste o valor para salvar.
                </div>
              )}
              <div className="flex justify-between border-t border-stone-200 pt-1 font-serif text-xl">
                <span>Total</span><span data-testid="orc-preview-total" className="text-[#083A6B]">{descontoExcede ? "—" : brl(totalCalc)}</span>
              </div>
              <div className="text-[10px] text-stone-500 italic">
                * Backend recalcula o total no momento de salvar (o campo acima é só preview).
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="btn-cancelar-orcamento" variant="outline"
              onClick={attemptClose}
              className="border-stone-200">
              Cancelar
            </Button>
            <Button data-testid="btn-submit-orcamento" onClick={submit} disabled={descontoExcede}
              className="bg-[#083A6B] hover:bg-[#22392E] text-white">
              {editingId ? "Salvar alterações" : "Criar orçamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MOBILE — Novo orçamento (wizard compacto) ============ */}
      <Dialog open={openMobile} onOpenChange={(v) => {
        if (!v) { if (isFormDirty()) { setDiscardOpen(true); } else { setOpenMobile(false); setForm(emptyForm()); } }
        else setOpenMobile(true);
      }}>
        <DialogContent
          data-testid="orc-form-mobile-dialog"
          className="p-0 gap-0 max-w-full w-screen h-[100dvh] sm:h-[95vh] sm:max-w-lg overflow-hidden flex flex-col"
          onPointerDownOutside={(e) => { if (isFormDirty()) { e.preventDefault(); setDiscardOpen(true); } }}
          onInteractOutside={(e) => { if (isFormDirty()) { e.preventDefault(); setDiscardOpen(true); } }}
          onEscapeKeyDown={(e) => { if (isFormDirty()) { e.preventDefault(); setDiscardOpen(true); } }}>
          <DialogHeader className="px-4 pt-4 pb-3 border-b border-stone-200">
            <DialogTitle className="font-serif text-xl">Novo orçamento</DialogTitle>
            <p className="text-[11px] text-stone-500">Fluxo rápido para atendimento comercial no celular.</p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 text-sm">
            {/* 1. CLIENTE */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">1. Cliente</div>
              <div className="inline-flex rounded-md border border-stone-200 overflow-hidden text-xs mb-3">
                <button type="button" data-testid="orc-mob-cliente-mode-existente"
                  onClick={() => setForm({ ...form, cliente_mode: "existente" })}
                  className={`px-3 py-2 ${form.cliente_mode === "existente" ? "bg-[#083A6B] text-white" : "bg-white text-stone-600"}`}>
                  Hóspede cadastrado
                </button>
                <button type="button" data-testid="orc-mob-cliente-mode-manual"
                  onClick={() => setForm({ ...form, cliente_mode: "manual" })}
                  className={`px-3 py-2 ${form.cliente_mode === "manual" ? "bg-[#083A6B] text-white" : "bg-white text-stone-600"}`}>
                  Novo cliente
                </button>
              </div>
              {form.cliente_mode === "existente" ? (
                <div className="space-y-2">
                  <Label>Hóspede *</Label>
                  <Select value={form.hospede_id} onValueChange={(v) => setForm({ ...form, hospede_id: v })}>
                    <SelectTrigger data-testid="orc-mob-select-hospede" className="h-11"><SelectValue placeholder="Selecione um hóspede" /></SelectTrigger>
                    <SelectContent>{hospedes.map((h) => <SelectItem key={h.id} value={h.id}>{h.nome}</SelectItem>)}</SelectContent>
                  </Select>
                  <Label className="pt-2">Empresa (opcional)</Label>
                  <Select value={form.empresa_id || NONE} onValueChange={(v) => setForm({ ...form, empresa_id: v === NONE ? "" : v })}>
                    <SelectTrigger data-testid="orc-mob-select-empresa" className="h-11"><SelectValue placeholder="Sem empresa" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Sem empresa</SelectItem>
                      {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[10px] text-stone-500 italic">Cliente comercial ≠ hóspede. Nenhum hóspede será criado agora.</div>
                  <Label>Nome / Razão social *</Label>
                  <Input data-testid="orc-mob-cm-nome" className="h-11" value={form.cliente_manual.nome}
                    onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, nome: e.target.value } })} />
                  <Label>WhatsApp / Telefone</Label>
                  <Input data-testid="orc-mob-cm-telefone" inputMode="tel" className="h-11" value={form.cliente_manual.telefone}
                    onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, telefone: e.target.value } })} />
                  <Label>E-mail</Label>
                  <Input data-testid="orc-mob-cm-email" type="email" inputMode="email" className="h-11" value={form.cliente_manual.email}
                    onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, email: e.target.value } })} />
                  <Label>CPF / CNPJ</Label>
                  <Input data-testid="orc-mob-cm-cpf-cnpj" className="h-11" value={form.cliente_manual.cpf_cnpj}
                    onChange={(e) => setForm({ ...form, cliente_manual: { ...form.cliente_manual, cpf_cnpj: e.target.value } })} />
                </div>
              )}
            </section>

            {/* 2. POUSADA */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">2. Pousada</div>
              <Select value={form.pousada_id} onValueChange={(v) => setForm({ ...form, pousada_id: v, itens: [] })}>
                <SelectTrigger data-testid="orc-mob-select-pousada" className="h-11"><SelectValue placeholder="Selecione a pousada" /></SelectTrigger>
                <SelectContent>{pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent>
              </Select>
            </section>

            {/* 3. DATAS */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">3. Datas</div>
              <SemDataFields form={form} setForm={setForm} prefix="orc-mob" noitesDasDatas={noitesDasDatas} />
              {!form.sem_data && (<>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <Label>Entrada *</Label>
                  <Input type="date" data-testid="orc-mob-data-entrada" className="h-11" value={form.data_entrada}
                    onChange={(e) => setForm({ ...form, data_entrada: e.target.value })} />
                </div>
                <div>
                  <Label>Saída *</Label>
                  <Input type="date" data-testid="orc-mob-data-saida" className="h-11" value={form.data_saida}
                    onChange={(e) => setForm({ ...form, data_saida: e.target.value })} />
                </div>
              </div>
              <div className="mt-2 text-xs text-stone-600" data-testid="orc-mob-diarias">
                {diarias > 0 ? <><b>{diarias}</b> noite{diarias > 1 ? "s" : ""}</> : <span className="text-[#D4A652]">Datas inválidas</span>}
              </div>
              </>)}
            </section>

            {/* 4. HOSPEDAGEM (quarto + tipo + pessoas) */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">4. Hospedagem</div>
              {!form.pousada_id && <div className="text-xs text-stone-500 italic">Selecione a pousada acima para carregar os quartos disponíveis.</div>}
              {form.pousada_id && (
                <div className="space-y-2">
                  <Label>Adicionar quarto</Label>
                  <Select value="" onValueChange={(v) => v && addItem(v, "individual")}>
                    <SelectTrigger data-testid="orc-mob-add-quarto" className="h-11"><SelectValue placeholder="Escolher quarto..." /></SelectTrigger>
                    <SelectContent>
                      {quartosDaPousada.filter((q) => !form.itens.some((it) => it.quarto_id === q.id)).map((q) => (
                        <SelectItem key={q.id} value={q.id}>Nº {q.numero} · {q.tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.itens.length === 0 && <div className="text-xs text-stone-500 italic">Nenhum quarto adicionado.</div>}
                  {form.itens.map((it, idx) => (
                    <div key={idx} data-testid={`orc-mob-item-${idx}`} className="border border-stone-200 rounded-md p-3 bg-stone-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-sm">Nº {it.quarto_numero} · {it.quarto_tipo}</div>
                        <button type="button" data-testid={`orc-mob-item-remover-${idx}`}
                          onClick={() => removeItem(idx)} className="text-[#D4A652] p-2"><Trash2 size={16} /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Tipo de item</Label>
                          <Select value={it.tipo_reserva}
                            onValueChange={(v) => setForm({ ...form, itens: form.itens.map((x, i) => i === idx ? { ...x, tipo_reserva: v } : x) })}>
                            <SelectTrigger data-testid={`orc-mob-item-tipo-${idx}`} className="h-10 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="individual">Individual</SelectItem>
                              <SelectItem value="pousada_inteira">Pousada inteira</SelectItem>
                              <SelectItem value="chacara_inteira">Chácara inteira</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Valor/diária</Label>
                          <Input type="number" step="0.01" data-testid={`orc-mob-item-diaria-${idx}`} className="h-10" value={it.valor_diaria}
                            disabled={valorManualAtivo}
                            onChange={(e) => setForm({ ...form, itens: form.itens.map((x, i) => i === idx ? { ...x, valor_diaria: e.target.value } : x) })} />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px]">Nº pessoas neste quarto</Label>
                          <Input type="number" min="1" data-testid={`orc-mob-item-pessoas-${idx}`} className="h-10" value={it.num_pessoas}
                            onChange={(e) => setForm({ ...form, itens: form.itens.map((x, i) => i === idx ? { ...x, num_pessoas: e.target.value } : x) })} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <Label>Nº de pessoas (total do orçamento)</Label>
                <Input type="number" min="1" inputMode="numeric" data-testid="orc-mob-num-pessoas" className="h-11" value={form.num_pessoas}
                  onChange={(e) => setForm({ ...form, num_pessoas: e.target.value })} />
              </div>
            </section>

            {/* 5. CAFÉ */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">5. Café da manhã</div>
              <Select value={form.cafe_tipo} onValueChange={(v) => setForm({ ...form, cafe_tipo: v })}>
                <SelectTrigger data-testid="orc-mob-cafe-tipo" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CAFE_OPTS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}{c.price ? ` (R$ ${c.price.toFixed(2)}/pessoa)` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.cafe_tipo === "manual" && (
                <div className="mt-2">
                  <Label>Valor manual por pessoa (R$)</Label>
                  <Input type="number" step="0.01" min="0" inputMode="decimal" data-testid="orc-mob-cafe-manual" className="h-11"
                    value={form.cafe_valor_pessoa_manual}
                    onChange={(e) => setForm({ ...form, cafe_valor_pessoa_manual: e.target.value })} />
                </div>
              )}
            </section>

            {/* 6. DESCONTO */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">6. Desconto (opcional)</div>
              <Input type="number" step="0.01" min="0" inputMode="decimal" data-testid="orc-mob-desconto" className="h-11"
                value={form.desconto} onChange={(e) => setForm({ ...form, desconto: e.target.value })} placeholder="0,00" />
              {descontoExcede && (
                <div data-testid="orc-mob-aviso-desconto-input" className="mt-1 text-xs text-red-700">
                  Desconto maior que o subtotal ({brl(subtotalHosp + subtotalCafe)}).
                </div>
              )}
            </section>

            {/* 7. OBSERVAÇÃO */}
            <section>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">7. Observação (opcional)</div>
              <Textarea data-testid="orc-mob-observacao" className="min-h-[80px]" value={form.observacao}
                onChange={(e) => setForm({ ...form, observacao: e.target.value })} placeholder="Notas internas / condições especiais..." />
            </section>

            {/* 8. RESUMO */}
            <section data-testid="orc-mob-resumo" className="border border-stone-200 rounded-md p-3 bg-[#FFFFFF]">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">8. Resumo</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div className="text-stone-500">Cliente</div>
                <div className="text-right text-stone-800 truncate">
                  {form.cliente_mode === "existente"
                    ? (hospedes.find((h) => h.id === form.hospede_id)?.nome || "—")
                    : (form.cliente_manual.nome || "—")}
                </div>
                <div className="text-stone-500">Pousada</div>
                <div className="text-right text-stone-800 truncate">{pousadaSel?.nome || "—"}</div>
                <div className="text-stone-500">Período</div>
                <div className="text-right text-stone-800" data-testid="orc-mob-resumo-periodo">{form.sem_data ? "Datas a definir" : `${fmtDate(form.data_entrada)} → ${fmtDate(form.data_saida)}`}</div>
                <div className="text-stone-500">Diárias</div>
                <div className="text-right text-stone-800">{diarias}</div>
                <div className="text-stone-500">Quartos</div>
                <div className="text-right text-stone-800">{form.itens.length}</div>
                <div className="text-stone-500">Pessoas</div>
                <div className="text-right text-stone-800">{form.num_pessoas}</div>
                <div className="text-stone-500">Café</div>
                <div className="text-right text-stone-800">{CAFE_OPTS.find((c) => c.key === form.cafe_tipo)?.label || "—"}</div>
                <div className="text-stone-500">Hospedagem</div>
                <div className="text-right text-stone-800">{brl(subtotalHosp)}</div>
                <div className="text-stone-500">Café subtotal</div>
                <div className="text-right text-stone-800">{brl(subtotalCafe)}</div>
                <div className="text-stone-500">Desconto</div>
                <div className="text-right text-[#D4A652]">− {brl(desc)}</div>
              </div>
              {descontoExcede && (
                <div data-testid="orc-mob-aviso-desconto" className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  O desconto é maior que o subtotal ({brl(subtotalHosp + subtotalCafe)}). Ajuste o valor para salvar.
                </div>
              )}
            </section>
          </div>

          {/* Sticky footer: TOTAL + Salvar */}
          <div className="border-t border-stone-200 px-4 py-3 bg-white flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Total estimado</div>
              <div data-testid="orc-mob-total" className="font-serif text-2xl text-[#083A6B] leading-none">{descontoExcede ? "—" : brl(totalCalc)}</div>
              <div className="text-[10px] text-stone-400">confirmado pelo servidor</div>
            </div>
            <Button data-testid="btn-orc-mob-salvar" onClick={submit} disabled={descontoExcede}
              className="h-12 px-5 bg-[#083A6B] hover:bg-[#22392E] text-white text-sm">
              <CheckCircle2 size={18} className="mr-2" /> Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* ============ Confirmação de descarte (RH-VISUAL-03) ============ */}
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent className="max-w-md" data-testid="orc-discard-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#D4A652]">Descartar orçamento?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-stone-700 space-y-2 py-2">
            <p>Você preencheu dados neste orçamento que ainda <b>não foram salvos</b>.</p>
            <p>Se sair agora, essas informações serão <b>perdidas</b> e não poderão ser recuperadas.</p>
          </div>
          <DialogFooter>
            <Button data-testid="btn-descartar-continuar" variant="outline"
              onClick={() => setDiscardOpen(false)}
              className="border-stone-200">
              Continuar preenchendo
            </Button>
            <Button data-testid="btn-descartar-confirmar" onClick={confirmDiscard}
              className="bg-[#D4A652] hover:bg-[#B4472B] text-white">
              Descartar e fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Detalhe (visualização profissional) ============ */}
      <Dialog open={detOpen} onOpenChange={(v) => { setDetOpen(v); if (!v) { setEmailHistoryFilter(""); setOrcShareUrl(null); setCopyFeedback(null); setOpenFailureKey(null); setQrOpen(false); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="orc-detalhe">
          {detTarget && (
            <div className="p-2 print:p-0">
              {/* Cabeçalho institucional */}
              <div className="flex items-start justify-between border-b border-stone-200 pb-4 mb-4">
                <div>
                  <div className="font-brand text-4xl leading-none text-stone-900">{ORCAMENTO_HEADER.marca}</div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 mt-1">{ORCAMENTO_HEADER.slogan}</div>
                  <div className="mt-3 text-xs text-stone-600 space-y-0.5">
                    <div><b>{ORCAMENTO_HEADER.razao_social}</b> · CNPJ {ORCAMENTO_HEADER.cnpj}</div>
                    <div>Nome fantasia: {ORCAMENTO_HEADER.nome_fantasia}</div>
                    <div>{ORCAMENTO_HEADER.endereco}</div>
                    <div>Tel: {ORCAMENTO_HEADER.telefone} · {ORCAMENTO_HEADER.email}</div>
                    {ORCAMENTO_HEADER.website && (
                      <div>{ORCAMENTO_HEADER.website}</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-[#D4A652] font-bold">Orçamento</div>
                  <div className="font-mono text-lg" data-testid="orc-detalhe-numero">{detTarget.numero}</div>
                  <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full mt-1 ${STATUS_TONE[detTarget.status]}`}>
                    {STATUS_LABEL[detTarget.status]}
                  </span>
                </div>
              </div>

              {/* Info cliente / período */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-widest text-stone-500">
                  Cliente {detTarget.cliente_tipo === "manual" && <span className="text-[#D4A652]">· comercial</span>}
                </div>
                  <div className="font-medium">{detTarget.cliente_snapshot?.nome || detTarget.hospede_nome}</div>
                  {detTarget.empresa_nome && <div className="text-xs text-stone-500">{detTarget.empresa_nome}</div>}
                  {detTarget.cliente_snapshot?.telefone && (
                    <div className="text-[11px] text-stone-500" data-testid={`orc-det-cli-tel-${detTarget.id}`}>
                      {detTarget.cliente_snapshot.telefone}
                    </div>
                  )}
                  {detTarget.cliente_snapshot?.email && (
                    <div className="text-[11px] text-stone-500" data-testid={`orc-det-cli-email-${detTarget.id}`}>
                      {detTarget.cliente_snapshot.email}
                    </div>
                  )}
                  {detTarget.cliente_snapshot?.cpf_cnpj && (
                    <div className="text-[11px] text-stone-500">{detTarget.cliente_snapshot.cpf_cnpj}</div>
                  )}
                </div>
                <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Pousada</div>
                  <div className="font-medium">{detTarget.pousada_nome}</div>
                </div>
                <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Período</div>
                  <div data-testid="orc-detalhe-periodo">{detTarget.sem_data ? "Datas da hospedagem: a definir pelo cliente" : `${fmtDate(detTarget.data_entrada)} → ${fmtDate(detTarget.data_saida)}`}</div>
                  <div className="text-xs text-stone-500">{detTarget.diarias} noite(s) · {detTarget.num_pessoas} pessoa(s)</div>
                </div>
                {detTarget.validade_data && (
                  <div><div className="text-[10px] uppercase tracking-widest text-stone-500">Validade</div>
                    <div>{fmtDate(detTarget.validade_data)}</div></div>
                )}
              </div>

              {/* Itens */}
              <div className="border border-stone-200 rounded-md overflow-hidden mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
                    <tr><th className="text-left p-2">Descrição</th><th className="text-right p-2">Diária</th><th className="text-right p-2">Subtotal</th></tr>
                  </thead>
                  <tbody>
                    {(detTarget.itens || []).map((it, i) => (
                      <tr key={i} className="border-t border-stone-100">
                        <td className="p-2">
                          <div className="font-medium">Nº {it.quarto_numero} · {it.quarto_tipo}</div>
                          <div className="text-[10px] text-stone-500 uppercase tracking-widest">{it.tipo_reserva}</div>
                        </td>
                        <td className="p-2 text-right">{detTarget.modo_valor === "manual" && detTarget.sem_data ? "—" : brl(it.valor_diaria)}</td>
                        <td className="p-2 text-right">{detTarget.modo_valor === "manual" && detTarget.sem_data ? "—" : brl(it.valor_diaria * detTarget.diarias)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totais */}
              <div className="bg-[#FFFFFF] border border-stone-200 rounded-md p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Hospedagem</span><span>{brl(detTarget.subtotal_hospedagem)}</span></div>
                <div className="flex justify-between"><span>Café da manhã ({detTarget.cafe_tipo}) — {detTarget.num_pessoas} × {detTarget.diarias} × {brl(detTarget.cafe_valor_pessoa)}</span><span>{brl(detTarget.cafe_valor_total)}</span></div>
                <div className="flex justify-between text-xs text-stone-500"><span>Subtotal</span><span>{brl(detTarget.subtotal)}</span></div>
                {detTarget.desconto > 0 && (
                  <div className="flex justify-between text-[#D4A652]"><span>Desconto</span><span>− {brl(detTarget.desconto)}</span></div>
                )}
                <div className="flex justify-between border-t border-stone-200 pt-1 font-serif text-2xl">
                  <span>Total</span><span data-testid="orc-detalhe-total" className="text-[#083A6B]">{brl(detTarget.total)}</span>
                </div>
              </div>

              {detTarget.observacao && (
                <div className="mt-4 text-xs text-stone-600 border border-stone-100 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Observações</div>
                  {detTarget.observacao}
                </div>
              )}

              <div className="mt-4 text-[10px] text-stone-400 italic">
                Este documento é uma proposta de orçamento — <b>não constitui reserva confirmada</b>.
                Nenhum valor foi lançado no financeiro. A conversão em reserva depende de confirmação e pagamento.
              </div>

              {/* Histórico de E-mails (somente leitura) */}
              <div className="mt-4 border-t border-stone-200 pt-3" data-testid={`orc-email-history-${detTarget.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
                  Histórico de E-mails
                </div>
                {(!detTarget.email_history || detTarget.email_history.length === 0) ? (
                  <div className="text-xs text-stone-500 italic" data-testid={`orc-email-history-empty-${detTarget.id}`}>
                    Nenhum e-mail enviado ainda.
                  </div>
                ) : (() => {
                  const q = (emailHistoryFilter || "").trim().toLowerCase();
                  // Preserva o índice original (para manter testids únicos e estáveis)
                  const filtered = detTarget.email_history
                    .map((e, i) => ({ e, i }))
                    .filter(({ e }) => !q || (e.to || "").toLowerCase().includes(q));
                  return (
                    <>
                      <div className="mb-2 flex items-center gap-2">
                        <Input
                          data-testid={`input-orc-email-history-filter-${detTarget.id}`}
                          value={emailHistoryFilter}
                          onChange={(ev) => setEmailHistoryFilter(ev.target.value)}
                          placeholder="Buscar por e-mail..."
                          className="h-8 text-xs flex-1"
                          type="search"
                        />
                        <span
                          data-testid={`orc-email-history-count-${detTarget.id}`}
                          className="text-[10px] uppercase tracking-widest text-stone-500 whitespace-nowrap">
                          {filtered.length} de {detTarget.email_history.length} envios
                        </span>
                      </div>
                      {filtered.length === 0 ? (
                        <div className="text-xs text-stone-500 italic"
                          data-testid={`orc-email-history-noresult-${detTarget.id}`}>
                          Nenhum envio encontrado para este e-mail.
                        </div>
                      ) : (
                        <ul className="space-y-1.5" data-testid={`orc-email-history-list-${detTarget.id}`}>
                          {filtered.map(({ e, i }) => {
                            let quando = e.sent_at || "";
                            try {
                              if (e.sent_at) quando = new Date(e.sent_at).toLocaleString("pt-BR");
                            } catch { /* mantém string original */ }
                            const ok = (e.status || "").toLowerCase() === "sent";
                            return (
                              <li key={i}
                                data-testid={`orc-email-history-item-${detTarget.id}-${i}`}
                                className="flex flex-wrap items-center gap-2 text-xs text-stone-700 border border-stone-100 rounded-md px-2 py-1.5 bg-stone-50">
                                <span className="font-mono text-[11px] text-stone-500">{quando}</span>
                                <span className="text-stone-400">•</span>
                                <span className="font-medium">{e.to || "—"}</span>
                                {ok ? (
                                  <span
                                    data-testid={`orc-email-history-badge-sent-${detTarget.id}-${i}`}
                                    className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#EAF0EA] text-[#083A6B]">
                                    Enviado
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    data-testid={`orc-email-history-badge-failed-${detTarget.id}-${i}`}
                                    aria-expanded={openFailureKey === `${detTarget.id}::${i}`}
                                    aria-controls={`orc-email-failure-reason-${detTarget.id}-${i}`}
                                    title={e.status ? `Status técnico: ${e.status}` : undefined}
                                    onClick={() => setOpenFailureKey(
                                      openFailureKey === `${detTarget.id}::${i}` ? null : `${detTarget.id}::${i}`
                                    )}
                                    className="ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#D4A652] text-white font-semibold hover:bg-[#A64D30] focus:outline-none focus:ring-2 focus:ring-[#D4A652]/40 cursor-pointer">
                                    Falhou
                                  </button>
                                )}
                                {detTarget.status === "aprovado" && (e.to || "").trim() && (
                                  <button
                                    data-testid={`btn-orc-reenviar-${detTarget.id}-${i}`}
                                    onClick={() => enviarEmail(detTarget, { preset: e.to })}
                                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#083A6B]/40 text-[#083A6B] bg-white hover:bg-[#EAF0EA]">
                                    Reenviar
                                  </button>
                                )}
                                {!ok && openFailureKey === `${detTarget.id}::${i}` && (
                                  <div
                                    id={`orc-email-failure-reason-${detTarget.id}-${i}`}
                                    data-testid={`orc-email-failure-reason-${detTarget.id}-${i}`}
                                    role="note"
                                    className="basis-full text-[11px] text-[#7a3120] bg-[#FBE7DE] border border-[#D4A652]/30 rounded-md px-2 py-1.5 mt-1">
                                    {friendlyFailureReason(e.status)}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Ações rápidas de status */}
              <div className="flex flex-wrap gap-2 mt-4 border-t border-stone-200 pt-3">
                <button data-testid={`btn-pdf-orc-${detTarget.id}`}
                  onClick={() => gerarPdf(detTarget)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border border-[#8B6A1F]/40 text-[#5c460f] bg-[#FDF3E3] hover:bg-[#F8E9C6]">
                  <FileText size={12} className="inline mr-1" /> Gerar PDF
                </button>
                <button data-testid={`btn-wa-orc-${detTarget.id}`}
                  onClick={() => compartilharWhatsapp(detTarget)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#25D366] text-white hover:bg-[#1FBE5A]">
                  📱 Compartilhar no WhatsApp
                </button>
                <button data-testid={`btn-orc-copiar-link-${detTarget.id}`}
                  onClick={() => copiarLink(detTarget)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border border-[#083A6B]/40 text-[#083A6B] bg-white hover:bg-[#EAF0EA]">
                  📋 Copiar link
                </button>
                <button data-testid={`btn-orc-qr-code-${detTarget.id}`}
                  onClick={() => abrirQrCode(detTarget)}
                  disabled={qrLoading}
                  aria-expanded={qrOpen}
                  aria-controls={`orc-qr-code-${detTarget.id}`}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border border-[#083A6B]/40 text-[#083A6B] bg-white hover:bg-[#EAF0EA] disabled:opacity-50">
                  ▣ {qrOpen ? "Fechar QR" : "QR Code"}
                </button>
                {copyFeedback && (
                  <span
                    data-testid={`orc-copy-link-feedback-${detTarget.id}`}
                    className={`text-[11px] px-2 py-1.5 rounded-md ${
                      copyFeedback.kind === "ok"
                        ? "bg-[#EAF0EA] text-[#083A6B]"
                        : "bg-[#FBE7DE] text-[#D4A652]"
                    }`}>
                    {copyFeedback.text}
                  </span>
                )}
                <button data-testid={`btn-revoke-orc-${detTarget.id}`}
                  onClick={() => revogarCompartilhamento(detTarget)}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border border-[#D4A652]/40 text-[#D4A652] bg-white hover:bg-[#FBE7DE]">
                  🔒 Revogar compartilhamento
                </button>
                {detTarget.status === "aprovado" && (
                  <button data-testid={`btn-email-orc-${detTarget.id}`}
                    onClick={() => enviarEmail(detTarget)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#083A6B] text-white hover:bg-[#22392E]">
                    ✉️ Enviar por E-mail
                  </button>
                )}
                {detTarget.status === "aprovado" && !detTarget.reserva_ids?.length && (
                  <button data-testid={`btn-orc-converter-${detTarget.id}`}
                    onClick={() => converter(detTarget)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#083A6B] text-white hover:bg-[#22392E]">
                    <CheckCircle2 size={12} className="inline mr-1" /> Virar reserva (pré-reserva)
                  </button>
                )}
                {detTarget.reserva_ids?.length > 0 && (
                  <div data-testid={`orc-ja-convertido-${detTarget.id}`}
                    className="text-[11px] text-[#083A6B] bg-[#EAF0EA] px-3 py-1.5 rounded-md">
                    ✓ Convertido — {detTarget.reserva_ids.length} pré-reserva(s) gerada(s).
                    <a href="/admin/reservas" className="underline ml-1">Ver</a>
                  </div>
                )}
                {["enviado", "aprovado", "recusado", "expirado"].map((s) => (
                  detTarget.status !== s && (
                    <button key={s} data-testid={`btn-orc-set-${s}-${detTarget.id}`}
                      onClick={() => { changeStatus(detTarget, s); setDetOpen(false); }}
                      className="px-3 py-1.5 rounded-md text-xs font-medium border border-stone-200 hover:bg-stone-50">
                      Marcar como {STATUS_LABEL[s]}
                    </button>
                  )
                ))}
              </div>

              {/* ============ Painel QR Code (usa o mesmo share_url) ============ */}
              {qrOpen && orcShareUrl && (
                <div
                  id={`orc-qr-code-${detTarget.id}`}
                  data-testid={`orc-qr-code-${detTarget.id}`}
                  role="dialog"
                  aria-label="QR Code do orçamento"
                  className="mt-4 border border-stone-200 rounded-md bg-white p-4 flex flex-col items-center gap-2">
                  <div className="flex w-full items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">
                      QR Code do orçamento
                    </div>
                    <button
                      data-testid={`orc-qr-close-${detTarget.id}`}
                      onClick={() => setQrOpen(false)}
                      aria-label="Fechar QR Code"
                      className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-800 px-2 py-0.5">
                      Fechar
                    </button>
                  </div>
                  <div className="p-3 bg-white rounded-md border border-stone-100">
                    <QRCodeSVG
                      data-testid={`orc-qr-code-svg-${detTarget.id}`}
                      value={orcShareUrl}
                      size={192}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <button
                    data-testid={`btn-orc-qr-download-${detTarget.id}`}
                    onClick={() => {
                      try {
                        const wrap = document.querySelector(`[data-testid="orc-qr-code-${detTarget.id}"]`);
                        const svg = wrap && wrap.querySelector("svg");
                        if (!svg) { toast.error("Não foi possível preparar o QR para download."); return; }
                        // Alta resolução p/ impressão: 4×
                        const scale = 4;
                        const size = 192;
                        const pad = 24; // margem branca ao redor
                        const px = size * scale;
                        const total = px + pad * 2 * scale;
                        const svgStr = new XMLSerializer().serializeToString(svg);
                        const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
                        const svgUrl = URL.createObjectURL(svgBlob);
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement("canvas");
                          canvas.width = total; canvas.height = total;
                          const ctx = canvas.getContext("2d");
                          ctx.fillStyle = "#FFFFFF";
                          ctx.fillRect(0, 0, total, total);
                          ctx.drawImage(img, pad * scale, pad * scale, px, px);
                          canvas.toBlob((blob) => {
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            const nome = (detTarget.numero || detTarget.id || "orcamento")
                              .toString().replace(/[^A-Za-z0-9_-]+/g, "_");
                            a.href = url; a.download = `qr-orcamento-${nome}.png`;
                            document.body.appendChild(a); a.click(); document.body.removeChild(a);
                            setTimeout(() => { URL.revokeObjectURL(url); URL.revokeObjectURL(svgUrl); }, 500);
                          }, "image/png");
                        };
                        img.onerror = () => {
                          URL.revokeObjectURL(svgUrl);
                          toast.error("Não foi possível gerar o PNG.");
                        };
                        img.src = svgUrl;
                      } catch {
                        toast.error("Não foi possível gerar o PNG.");
                      }
                    }}
                    className="text-[10px] uppercase tracking-widest text-[#083A6B] border border-[#083A6B]/40 bg-white hover:bg-[#EAF0EA] rounded-md px-3 py-1">
                    ⬇ Baixar PNG
                  </button>
                  <div className="text-[11px] text-stone-500 text-center max-w-xs">
                    Escaneie com a câmera do celular para abrir o PDF.
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ Diálogo de Envio por E-mail (com edição opcional) ============ */}
      <Dialog open={emailDialogOpen} onOpenChange={(v) => { setEmailDialogOpen(v); if (!v) { setEmailInputError(""); } }}>
        <DialogContent className="max-w-md" data-testid="orc-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {emailDialogTarget?.isReenvio ? "Reenviar orçamento por e-mail" : "Enviar orçamento por e-mail"}
            </DialogTitle>
          </DialogHeader>
          {emailDialogTarget && (
            <div className="space-y-3 py-2">
              <div className="text-xs text-stone-500">
                Orçamento <b className="font-mono">{emailDialogTarget.orc.numero}</b> · {emailDialogTarget.orc.hospede_nome}
              </div>
              {emailDialogTarget.cadastroEmail && !emailDialogTarget.forceOverride ? (
                <div data-testid="orc-email-dialog-cadastro-view">
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Destinatário (do cadastro)</Label>
                  <div className="mt-1 px-3 py-2 border border-stone-200 rounded-md bg-stone-50 text-sm font-mono">
                    {emailDialogTarget.cadastroEmail}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-1">
                    O envio usará o e-mail cadastrado do hóspede.
                  </div>
                </div>
              ) : (
                <div data-testid="orc-email-dialog-override-view">
                  <Label htmlFor="orc-email-override" className="text-xs uppercase tracking-widest text-stone-500">
                    E-mail para este envio
                  </Label>
                  <Input id="orc-email-override" type="email"
                    data-testid="input-orc-email-override"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); if (emailInputError) setEmailInputError(""); }}
                    placeholder="cliente@exemplo.com"
                    className="mt-1" />
                  <div className="text-[11px] text-stone-500 mt-1">
                    {emailDialogTarget.isReenvio
                      ? "Este envio usará o e-mail informado, sem alterar o cadastro do hóspede."
                      : "O hóspede não possui e-mail cadastrado. Este endereço será usado apenas neste envio — o cadastro não será alterado."}
                  </div>
                  {emailInputError && (
                    <div data-testid="orc-email-dialog-erro" className="text-[11px] text-[#D4A652] mt-1">
                      {emailInputError}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="btn-orc-email-cancelar"
              onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
              Cancelar
            </Button>
            <Button data-testid="btn-orc-email-enviar"
              onClick={confirmarEnvioEmail} disabled={emailSending}
              className="bg-[#083A6B] hover:bg-[#22392E] text-white">
              {emailSending ? "Enviando..." : "Enviar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
